import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from '@saleshandy/icons';
import TransferOverlayTrigger, {
  TransferOverlayTriggerProps,
} from '../../../design-system/components/transfer/transfer-overlay-trigger';
import { Placement } from '../../../design-system/components/overlay/enums';
import { ExtendedOption } from '../../../design-system/components/transfer/transfer';
import { useInfiniteScroll } from '../../../hooks/use-infinite-scroll';

export interface VoicemailTemplateOption extends ExtendedOption {
  key: string;
  name: string;
  value?: string;
}

export interface VoicemailTemplateDropdownProps<
  T extends VoicemailTemplateOption
> extends Omit<
    TransferOverlayTriggerProps<T>,
    'showSearch' | 'children' | 'scrollContainerRef'
  > {
  /** Show or hide the search bar */
  showSearch?: boolean;
  /** When set with `onLoadMore`, scroll near bottom loads the next page */
  hasMore?: boolean;
  /** Fetch next page (e.g. dispatch paginated list request) */
  onLoadMore?: () => void;
  /** True while a "load more" request is in flight */
  isLoadingMore?: boolean;
  /** Selected template value */
  selectedValue?: string | number;
  /** Callback when template is selected */
  onSelectTemplate?: (template: T) => void;
  /** Callback when Send button is clicked */
  onSend?: () => void;
  /** Custom trigger button */
  triggerButton?: React.ReactElement;
  /** Children - optional, defaults to built-in trigger */
  children?: React.ReactElement;
  /** Container element for the overlay portal */
  container?: HTMLElement | (() => HTMLElement) | null;
  /** Popper configuration for positioning */
  popperConfig?: any;
}

const VoicemailTemplateDropdown = <T extends VoicemailTemplateOption>({
  showSearch = true,
  selectedValue,
  onSelectTemplate,
  onSend,
  triggerButton,
  options,
  optionRenderer,
  filterOption,
  optionFilterProp = 'name',
  searchPlaceholder = 'Search',
  placement = Placement.BottomStart,
  container,
  popperConfig,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  ...rest
}: VoicemailTemplateDropdownProps<T>) => {
  const [show, setShow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(
    (opt) => opt.key === String(selectedValue) || opt.key === selectedValue,
  );

  useInfiniteScroll({
    scrollContainerRef,
    hasMore,
    onLoadMore: onLoadMore ?? (() => undefined),
    isLoadingMore,
    enabled: show && Boolean(onLoadMore),
    rebindKey: show ? 1 : 0,
  });

  const handleChange = (selectedOptions: T[]) => {
    if (selectedOptions.length > 0 && onSelectTemplate) {
      onSelectTemplate(selectedOptions[0]);
      setShow(false);
    }
  };

  const defaultTrigger: React.ReactElement = (
    <button
      type="button"
      className="voicemail-template-dropdown-trigger"
      onClick={() => setShow(!show)}
    >
      <span className="voicemail-template-dropdown-trigger-text">
        {selectedOption?.name || 'Select template'}
      </span>
      <div className="voicemail-template-dropdown-icon">
        {show ? (
          <ChevronUp width={16} height={16} color="#808080" />
        ) : (
          <ChevronDown width={16} height={16} color="#808080" />
        )}
      </div>
    </button>
  );

  const triggerElement: React.ReactElement = triggerButton || defaultTrigger;

  return (
    <TransferOverlayTrigger<T>
      {...rest}
      show={show}
      onToggle={setShow}
      showSearch={showSearch}
      searchPlaceholder={searchPlaceholder}
      options={options}
      optionRenderer={optionRenderer}
      filterOption={filterOption}
      optionFilterProp={optionFilterProp}
      selectedOptionKey={selectedValue}
      onChange={handleChange}
      placement={placement}
      className="voicemail-template-dropdown"
      multiSelect={false}
      container={container}
      popperConfig={popperConfig}
      scrollContainerRef={scrollContainerRef}
      // eslint-disable-next-line react/no-children-prop
      children={triggerElement}
    />
  );
};

export default VoicemailTemplateDropdown;
